/**
 * VendorLedgerSection — Rich ledger view with date-range downloads, credit/debit
 * filter, enhanced row details. Used by both GiftCardVendorsPage (Admin/CA view)
 * and VendorLedgerPage (vendor self-view).
 *
 * Props:
 *   vendorId    : string (required)
 *   vendorName  : string (optional, for export filename)
 *   entries     : array  (required, ledger entries from /vendors/{id}/ledger)
 *   balance     : object (optional, { credit, debit, balance })
 *   showBalance : bool   (show 3 balance cards inside the section, default false
 *                         — outer page may already show their own balance cards)
 */
import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { FilePdf, FileXls, ArrowClockwise, CaretDown, CaretUp, FunnelSimple, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

// Range chip keys — user spec Feb 2026: "1 day/7 day/30 days/custom + All"
const RANGE_CHIPS = [
  { key: 'all',     label: 'All Time',   days: null },
  { key: '1d',      label: '1 Day',      days: 1 },
  { key: '7d',      label: '7 Days',     days: 7 },
  { key: '30d',     label: '30 Days',    days: 30 },
  { key: 'custom',  label: 'Custom',     days: null },
];

export default function VendorLedgerSection({
  vendorId,
  vendorName,
  entries = [],
  balance = null,
  showBalance = false,
  onRefresh,
}) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [backDatedOnly, setBackDatedOnly] = useState(false);
  const [rangeKey, setRangeKey] = useState('all');   // 'all' | '1d' | '7d' | '30d' | 'custom'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Ledger table hidden by default (user spec Feb 2026 — "esko hide kar k rakho,
  // ledger open karu tab aaye"). Admin explicitly clicks to expand.
  const [tableOpen, setTableOpen] = useState(false);

  // Chip click: sets range key + auto-fills from/to for the last-N-days chips
  const pickRange = (k) => {
    setRangeKey(k);
    if (k === 'all' || k === 'custom') {
      if (k === 'all') { setFromDate(''); setToDate(''); }
      return;
    }
    const chip = RANGE_CHIPS.find((c) => c.key === k);
    if (!chip?.days) return;
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - chip.days + 1);   // inclusive today
    const toISO = (d) => d.toISOString().slice(0, 10);
    setFromDate(toISO(from));
    setToDate(toISO(today));
  };

  const handleDownload = async (format) => {
    if (!vendorId) return;
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/vendors/${vendorId}/ledger/${format}${qs}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (vendorName || 'vendor').replace(/[^a-z0-9_-]+/gi, '_');
      const rangeSuffix = (fromDate || toDate) ? `_${fromDate || 'start'}_to_${toDate || 'today'}` : '';
      a.href = url;
      a.download = `ledger_${safeName}${rangeSuffix}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    }
  };

  // ─── Range-scoped filtered entries + running balance ───
  // 1. entriesAsc: oldest → newest, running balance accumulated → gives us
  //    the true opening balance = running balance BEFORE the first in-range
  //    entry, and closing balance = running balance AFTER the last in-range.
  // 2. Filter by rangeKey / fromDate / toDate. `all` → no filter.
  const { rangedEntries, rangedOpening, rangedCredit, rangedDebit, rangedClosing, allTimeClosing } = useMemo(() => {
    const asc = [...entries].slice().reverse();
    let run = 0;
    const withBal = asc.map((e) => {
      run += (e.type === 'credit' ? Number(e.amount || 0) : -Number(e.amount || 0));
      return { ...e, balance_after: Number(run.toFixed(2)) };
    });
    const finalClosing = withBal.length ? withBal[withBal.length - 1].balance_after : 0;

    // Range filter — inclusive on both sides. Date-only compare (YYYY-MM-DD).
    const inRange = (isoStr) => {
      if (rangeKey === 'all') return true;
      if (!fromDate && !toDate) return true;
      const dt = (isoStr || '').slice(0, 10);
      if (fromDate && dt < fromDate) return false;
      if (toDate && dt > toDate) return false;
      return true;
    };

    let opening = 0;
    let credit = 0;
    let debit = 0;
    const inRangeAsc = [];
    for (const e of withBal) {
      if (inRange(e.created_at)) {
        inRangeAsc.push(e);
        if (e.type === 'credit') credit += Number(e.amount || 0);
        else debit += Number(e.amount || 0);
      } else if (inRangeAsc.length === 0) {
        // Still before range → this becomes the running opening balance
        opening = e.balance_after;
      }
    }
    const closing = inRangeAsc.length
      ? inRangeAsc[inRangeAsc.length - 1].balance_after
      : opening;

    // Return in DESC order for table display
    return {
      rangedEntries: inRangeAsc.slice().reverse(),
      rangedOpening: opening,
      rangedCredit: credit,
      rangedDebit: debit,
      rangedClosing: closing,
      allTimeClosing: finalClosing,
    };
  }, [entries, rangeKey, fromDate, toDate]);

  const backDatedCountInRange = rangedEntries.filter((e) => e.back_dated).length;
  const filtered = rangedEntries
    .filter((e) => typeFilter === 'all' ? true : e.type === typeFilter)
    .filter((e) => backDatedOnly ? e.back_dated : true);
  // Use ranged balance in cards (falls back to prop for backward compat when
  // caller-supplied all-time balance is more accurate, e.g. rangeKey='all').
  const displayCredit = rangeKey === 'all' ? Number((balance || {}).credit || rangedCredit) : rangedCredit;
  const displayDebit  = rangeKey === 'all' ? Number((balance || {}).debit  || rangedDebit)  : rangedDebit;
  const displayClosing = rangeKey === 'all' ? Number((balance || {}).balance ?? allTimeClosing) : rangedClosing;

  return (
    <div className="space-y-3" data-testid={`vendor-ledger-section-${vendorId}`}>
      {/* ═══ Header — always visible, shows range + all-time balance + refresh ═══ */}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-gray-900">Ledger</h2>
        <span className="text-xs bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 rounded px-2 py-0.5 font-mono font-semibold" data-testid="ledger-alltime-balance">
          All-time · {fmt(allTimeClosing)}
        </span>
        <span className="text-xs text-gray-500">{entries.length} entries</span>
        <div className="ml-auto flex gap-2 flex-wrap">
          {onRefresh && (
            <Button size="sm" variant="outline" onClick={onRefresh} data-testid="ledger-section-refresh">
              <ArrowClockwise size={14} /> Refresh
            </Button>
          )}
        </div>
      </div>

      {/* ═══ Range chip row — 1 Day / 7 Days / 30 Days / Custom / All ═══ */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="ledger-range-chips">
        {RANGE_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => pickRange(c.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
              rangeKey === c.key
                ? 'bg-fuchsia-600 border-fuchsia-600 text-white shadow-sm'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            data-testid={`ledger-range-${c.key}`}
          >
            {c.label}
          </button>
        ))}
        {rangeKey === 'custom' && (
          <div className="flex items-center gap-1.5 ml-1">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-8 px-2 border border-gray-300 rounded text-xs"
              data-testid="ledger-section-from-date"
            />
            <span className="text-xs text-gray-500">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-8 px-2 border border-gray-300 rounded text-xs"
              data-testid="ledger-section-to-date"
            />
          </div>
        )}
        {rangeKey !== 'all' && (fromDate || toDate) && (
          <span className="text-[10px] text-gray-500 ml-1">
            Active: <b>{fromDate || 'start'} → {toDate || 'today'}</b>
          </span>
        )}
      </div>

      {/* ═══ Balance cards — RANGE-SCOPED. Always visible so admin always sees
              a summary without opening the full table. ═══ */}
      {(showBalance || true) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="bg-gray-50 border border-gray-300 rounded-lg p-3" data-testid="ledger-opening-balance">
            <div className="text-[11px] uppercase text-gray-600 font-semibold">Opening Balance</div>
            <div className="text-xl font-bold text-gray-900 mt-0.5">{fmt(rangedOpening)}</div>
            <div className="text-[10px] text-gray-500">
              {rangeKey === 'all' ? 'Starting point' : 'Before range'}
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-[11px] uppercase text-emerald-700 font-semibold">Credited (Earned)</div>
            <div className="text-xl font-bold text-emerald-900 mt-0.5">{fmt(displayCredit)}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-[11px] uppercase text-red-700 font-semibold">Debited (Paid/Reversed)</div>
            <div className="text-xl font-bold text-red-900 mt-0.5">{fmt(displayDebit)}</div>
          </div>
          <div className={`${displayClosing >= 0 ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900' : 'bg-amber-50 border-amber-200 text-amber-900'} border rounded-lg p-3`} data-testid="ledger-closing-balance">
            <div className="text-[11px] uppercase font-semibold opacity-80">Closing Balance</div>
            <div className="text-2xl font-bold mt-0.5">{fmt(displayClosing)}</div>
            <div className="text-[10px] opacity-70">
              {rangeKey === 'all' ? 'Current net' : 'End of range'}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Expand toggle — opens the full transaction table + downloads ═══ */}
      <button
        type="button"
        onClick={() => setTableOpen((s) => !s)}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border transition ${
          tableOpen
            ? 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
        data-testid="ledger-toggle-table"
      >
        {tableOpen ? (
          <>
            <CaretUp size={16} weight="bold" /> Close Ledger — {rangedEntries.length} entries hidden
          </>
        ) : (
          <>
            <CaretDown size={16} weight="bold" /> Open Ledger — {rangedEntries.length} entries in current range
          </>
        )}
      </button>

      {/* ═══ Expanded section — downloads + credit/debit tabs + full table ═══ */}
      {tableOpen && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <FunnelSimple size={16} className="text-gray-500" />
            <Button size="sm" variant="outline" onClick={() => handleDownload('pdf')}
              data-testid="ledger-section-download-pdf"
              className="gap-1 text-red-600 border-red-300 hover:bg-red-50">
              <FilePdf size={14} weight="duotone" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleDownload('excel')}
              data-testid="ledger-section-download-excel"
              className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50">
              <FileXls size={14} weight="duotone" /> Excel
            </Button>
            <div className="ml-auto flex gap-2 flex-wrap items-center">
              {backDatedCountInRange > 0 && (
                <button
                  type="button"
                  onClick={() => setBackDatedOnly((s) => !s)}
                  className={`px-3 py-1 text-xs font-semibold rounded border flex items-center gap-1 transition ${
                    backDatedOnly
                      ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                      : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                  }`}
                  title="Filter ledger to only rows that were back-dated"
                  data-testid="ledger-section-backdated-toggle"
                >
                  <ClockCounterClockwise size={12} weight="bold" />
                  Back-Dated Only
                  <span className={`text-[10px] font-bold rounded-full px-1.5 ${backDatedOnly ? 'bg-white text-amber-700' : 'bg-amber-600 text-white'}`}>
                    {backDatedCountInRange}
                  </span>
                </button>
              )}
              {[
                ['all', 'All'],
                ['credit', 'Credits Only'],
                ['debit', 'Debits Only'],
              ].map(([k, label]) => (
                <button key={k} onClick={() => setTypeFilter(k)}
                  className={`px-3 py-1 text-xs font-semibold rounded border ${typeFilter === k ? 'bg-fuchsia-600 border-fuchsia-600 text-white' : 'bg-white border-gray-300'}`}
                  data-testid={`ledger-section-filter-${k}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Ledger table */}
          <div className="bg-white border rounded-xl overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">No entries in this range</div>
            ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date &amp; Time</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Balance After</th>
                  <th className="px-3 py-2 text-left">Payment / Ref</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                  <th className="px-3 py-2 text-left">By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const balAfter = e.balance_after;
                  return (
                  <tr key={e.id || `${e.created_at}-${i}`} className={`border-t hover:bg-gray-50 ${e.back_dated && backDatedOnly ? 'bg-amber-50/30' : ''}`} data-testid={`gc-ledger-row-${e.id || i}`}>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>{fmtDate(e.created_at)}</span>
                        {e.back_dated && backDatedOnly && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 rounded px-1 py-0.5 cursor-help"
                            title={e.recorded_at
                              ? `Back-dated entry — actually recorded on ${new Date(e.recorded_at).toLocaleString('en-IN')}`
                              : 'Back-dated entry'}
                            data-testid={`ledger-backdated-pill-${e.id || i}`}
                          >
                            <ClockCounterClockwise size={9} weight="bold" />
                            Back-Dated
                          </span>
                        )}
                      </div>
                      {e.back_dated && backDatedOnly && e.recorded_at && (
                        <div className="text-[9px] text-amber-700 mt-0.5">
                          rec {new Date(e.recorded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${e.type === 'credit' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {e.type === 'credit' ? 'CR (+)' : 'DR (−)'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{e.category}</td>
                    <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${e.type === 'credit' ? 'text-emerald-700' : 'text-red-700'}`}>
                      {e.type === 'credit' ? '+' : '−'} {fmt(e.amount)}
                    </td>
                    <td className={`px-3 py-2 text-right font-bold whitespace-nowrap ${balAfter >= 0 ? 'text-fuchsia-700' : 'text-amber-700'}`} data-testid={`balance-after-${e.id || i}`}>
                      {balAfter !== undefined ? fmt(balAfter) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.payment_mode_label && <div className="font-semibold">{e.payment_mode_label}</div>}
                      {!e.payment_mode_label && e.payment_mode && <div className="font-semibold">{e.payment_mode.toUpperCase()}</div>}
                      {e.account_holder && <div className="text-gray-500">{e.account_holder} • {e.account_number}</div>}
                      {e.upi_txn_id && <div className="text-gray-500">UPI: {e.upi_txn_id}</div>}
                      {e.receipt_number && <div className="text-gray-500">Receipt: {e.receipt_number}</div>}
                      {e.reference_no && <div className="text-gray-500 font-mono">{e.reference_no}</div>}
                      {e.ticket_no && <div className="text-indigo-600 font-mono text-[10px]">{e.ticket_no}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 max-w-[280px] truncate" title={e.remarks}>
                      {e.remarks || '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{e.created_by_name || '—'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
